import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTelegramAutoLogin } from '@/hooks/use-tg-auth';
import { isInTelegram } from '@/lib/telegram';
import { AuthLayout } from '@/components/layout/auth-layout';
import { Button } from '@/components/ui/button';
import { PageLoader } from '@/components/common/page-loader';

export function TelegramBoot({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  if (
    pathname === '/knowledge'
    || pathname.startsWith('/knowledge/')
    || pathname === '/blog'
    || pathname.startsWith('/blog/')
    || pathname === '/legal'
    || pathname.startsWith('/legal/')
  ) {
    return <>{children}</>;
  }

  return <TelegramAuthBoot>{children}</TelegramAuthBoot>;
}

function TelegramAuthBoot({ children }: { children: ReactNode }) {
  const auth = useTelegramAutoLogin();
  if (isInTelegram() && !auth.consentGranted) {
    return <TelegramConsent />;
  }
  if (auth.error) {
    return (
      <AuthLayout title="Не удалось войти" subtitle="Проверь соединение и попробуй ещё раз.">
        <Button className="w-full" onClick={auth.retry}>Повторить</Button>
      </AuthLayout>
    );
  }
  const done = auth.done;
  if (!done) return <PageLoader />;
  return <>{children}</>;
}

function TelegramConsent() {
  const [draft, setDraft] = useState(() => {
    try {
      const stored = JSON.parse(sessionStorage.getItem('aniphys:tg-consent-draft') || '{}');
      return {
        offer: stored.offer === true,
        personal: stored.personal === true,
        ai: stored.ai === true,
      };
    } catch {
      return { offer: false, personal: false, ai: false };
    }
  });
  const accepted = draft.offer && draft.personal;

  const updateDraft = (change: Partial<typeof draft>) => {
    const next = { ...draft, ...change };
    setDraft(next);
    sessionStorage.setItem('aniphys:tg-consent-draft', JSON.stringify(next));
  };

  const continueToApp = () => {
    if (!accepted) return;
    localStorage.setItem('aniphys:tg-legal:2026-07-29', '1');
    localStorage.setItem('aniphys:tg-ai-consent:2026-07-29', draft.ai ? '1' : '0');
    sessionStorage.removeItem('aniphys:tg-consent-draft');
    window.location.reload();
  };

  return (
    <AuthLayout
      title="Перед началом"
      subtitle="Подтверди условия обработки данных для работы Aniphys в Telegram."
    >
      <div className="space-y-4">
        <ConsentRow checked={draft.offer} onChange={(checked) => updateDraft({ offer: checked })}>
          Принимаю{' '}
          <Link className="underline" to="/legal/offer">договор-оферту</Link>
          {' '}и{' '}
          <Link className="underline" to="/legal/terms">пользовательское соглашение</Link>.
        </ConsentRow>
        <ConsentRow
          checked={draft.personal}
          onChange={(checked) => updateDraft({ personal: checked })}
        >
          Даю согласие на{' '}
          <Link className="underline" to="/legal/consent">обработку персональных данных</Link>,
          включая данные о здоровье, по{' '}
          <Link className="underline" to="/legal/privacy">политике конфиденциальности</Link>.
        </ConsentRow>
        <ConsentRow checked={draft.ai} onChange={(checked) => updateDraft({ ai: checked })}>
          Разрешаю AI-функциям передавать текст запросов и связанные данные профиля в API DeepSeek. Необязательно.
        </ConsentRow>
        <Button className="w-full" size="lg" disabled={!accepted} onClick={continueToApp}>
          Продолжить
        </Button>
      </div>
    </AuthLayout>
  );
}

function ConsentRow({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 text-xs leading-relaxed text-[var(--text-muted)]">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--accent)]"
      />
      <span>{children}</span>
    </label>
  );
}
